import { Button, Input, Label } from '@betstarters/ui'
import { useTranslation } from 'react-i18next'
import { ErrorBanner } from './catalog'
import { Combobox } from './controls'
import type { ReferenceCountry } from '../hooks/useReference'

/**
 * The bank create/edit form, shared by the PSP list dialog and the Banks tab of
 * a country — one definition of the fields, their limits and their hints.
 */

export interface BankFormValue {
  bank: string
  swift: string
  countryId: string
}

export const emptyBankForm: BankFormValue = { bank: '', swift: '', countryId: '' }

export function isBankFormValid(v: BankFormValue): boolean {
  return v.bank.trim() !== '' && v.countryId !== ''
}

export function BankForm({
  value,
  onChange,
  onSubmit,
  onCancel,
  countries,
  lockCountry,
  pending,
  error,
  submitLabel,
}: {
  value: BankFormValue
  onChange: (v: BankFormValue) => void
  onSubmit: () => void
  onCancel: () => void
  countries: ReferenceCountry[]
  /** Set on a country's own page: the country is the page, not a choice. */
  lockCountry?: boolean
  pending: boolean
  error: string
  submitLabel: string
}) {
  const { t } = useTranslation('app')
  const set = (patch: Partial<BankFormValue>) => onChange({ ...value, ...patch })

  return (
    <form
      className="flex max-w-xl flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault()
        if (isBankFormValid(value)) onSubmit()
      }}
    >
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="bank-name">{t('banks.columns.name')}</Label>
        <Input
          id="bank-name"
          value={value.bank}
          onChange={(e) => set({ bank: e.target.value })}
          maxLength={100}
          placeholder="Banco de Chile"
          autoFocus
        />
        <p className="text-xs text-muted-foreground">{t('banks.nameHint')}</p>
      </div>

      {!lockCountry && (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="bank-country">{t('banks.columns.country')}</Label>
          <Combobox
            value={value.countryId}
            onValueChange={(v) => set({ countryId: v })}
            items={countries.map((c) => ({
              value: String(c.id),
              label: `${c.isoCode2} — ${c.name}`,
            }))}
            placeholder={t('banks.pickCountry')}
            className="w-full"
          />
        </div>
      )}

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="bank-swift">{t('banks.columns.swift')}</Label>
        <Input
          id="bank-swift"
          value={value.swift}
          onChange={(e) => set({ swift: e.target.value.toUpperCase() })}
          maxLength={50}
          className="font-mono"
          placeholder="BCHICLRM"
        />
        <p className="text-xs text-muted-foreground">{t('banks.swiftHint')}</p>
      </div>

      <ErrorBanner message={error} />

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" disabled={pending || !isBankFormValid(value)}>
          {submitLabel}
        </Button>
      </div>
    </form>
  )
}
